import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, KeyRound, Mail } from "lucide-react";
import { Toaster, toast } from "sonner";
import { RegisterLayout, Input, Textarea, SubmitButton } from "../components/RegisterLayout";
import { SponsorPicker } from "../components/SponsorPicker";
import { SecurityPulseGate } from "../components/SecurityPulseGate";
import { BUYER_BADGES, CATEGORY_INFO_LABEL } from "../lib/badges";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { validateGstin } from "../lib/gst";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuyerRegister() {
  const { registerBuyer } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const emailParam = searchParams.get("email") || "";
  const [category, setCategory] = useState(inviteToken ? "super_stockist" : "");
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null); // resolved invite context from `/invites/{token}`
  const [inviteError, setInviteError] = useState("");
  const [form, setForm] = useState({
    business_name: "", contact_person: "", email: emailParam, phone: "", gst_number: "", address: "", category_info: "", password: "",
    // Custodian-only
    bank_holder_name: "", bank_name: "", ifsc: "", account_number: "",
  });
  const [gstErr, setGstErr] = useState("");
  const [sponsor, setSponsor] = useState(null); // { id, business_name, vendor_code, category, state } | null
  const [pulseOpen, setPulseOpen] = useState(false);

  // Resolve invite token on mount
  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API}/invites/${inviteToken}`)
      .then((r) => {
        const inv = r.data;
        setInvite(inv);
        setSponsor({
          id: inv.sponsor_id,
          business_name: inv.sponsor_business_name,
          vendor_code: inv.sponsor_vendor_code,
          category: inv.sponsor_category,
          state: "",
        });
        setForm((f) => ({
          ...f,
          email: f.email || inv.email || "",
          business_name: f.business_name || inv.business_name || "",
        }));
        setCategory("super_stockist");
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || "Invite link is invalid or expired.";
        setInviteError(msg);
        toast.error(msg);
      });
  }, [inviteToken]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "gst_number") setGstErr("");
  };

  // Reset sponsor if user changes category away from super_stockist (only when no invite locks the flow)
  const selectCategory = (k) => {
    if (invite) return; // locked to super_stockist
    setCategory(k);
    if (k !== "super_stockist") setSponsor(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Please select a partner category."); return; }
    const ge = validateGstin(form.gst_number);
    if (ge) { setGstErr(ge); toast.error(ge); return; }
    if (category === "custodian") {
      for (const [k, l] of [["bank_holder_name","Account holder"], ["bank_name","Bank"], ["ifsc","IFSC"], ["account_number","Account number"]]) {
        if (!form[k]?.trim()) { toast.error(`${l} is required for Custodian accounts.`); return; }
      }
    }
    // Security Pulse gate — every buyer signup must verify their email
    // via a 6-digit code before the account can be created.
    setPulseOpen(true);
  };

  const finishRegister = async (otp_code) => {
    setPulseOpen(false);
    setLoading(true);
    try {
      const payload = {
        category,
        business_name: form.business_name,
        contact_person: form.contact_person,
        email: form.email,
        phone: form.phone,
        gst_number: form.gst_number.trim().toUpperCase(),
        address: form.address,
        category_info: form.category_info,
        password: form.password,
        otp_code,
      };
      if (category === "super_stockist") {
        if (inviteToken) payload.invite_token = inviteToken;
        else if (sponsor) payload.parent_id = sponsor.id;
      }
      if (category === "custodian") {
        payload.bank_holder_name = form.bank_holder_name.trim();
        payload.bank_name = form.bank_name.trim();
        payload.ifsc = form.ifsc.trim().toUpperCase();
        payload.account_number = form.account_number.trim();
      }
      await registerBuyer(payload);
      toast.success("Account created. Welcome to the Vinci network.");
      nav("/account?welcome=jitsi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterLayout
      kicker="/ Buyer Registration"
      title="Choose your partner tier"
      subtitle="Select the category that matches your business. Tiers I–IV place orders on the network; Level-2 sponsors (Introducer, Custodian, Guarantor) sit above dependent Super-Stockists."
    >
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.values(BUYER_BADGES).map((b) => {
          const selected = category === b.key;
          return (
            <button
              key={b.key}
              type="button"
              data-testid={`category-${b.key}`}
              onClick={() => selectCategory(b.key)}
              className={`group text-left rounded-sm border p-6 relative overflow-hidden transition-all duration-300 ${selected ? "border-white/40" : "border-white/10 hover:border-white/25"}`}
              style={selected ? { boxShadow: `0 0 30px ${b.glow}33` } : {}}
            >
              {selected && (
                <span className="absolute top-4 right-4 grid place-items-center w-6 h-6 rounded-full text-black" style={{ background: b.to }}>
                  <Check size={14} />
                </span>
              )}
              <span className="grid place-items-center w-11 h-11 rounded-full text-black" style={{ background: `linear-gradient(135deg, ${b.from}, ${b.to})` }}>
                <b.Icon size={18} />
              </span>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] mt-5" style={{ color: b.to }}>{b.tier}</div>
              <div className="font-serif text-xl mt-1">{b.label}</div>
              <p className="font-body text-xs text-[#8A8A82] mt-2 leading-relaxed">{b.blurb}</p>
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {category && (
          <motion.form
            data-testid="buyer-form"
            onSubmit={submit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-14 pt-10 border-t border-white/10">
              <h3 className="font-serif text-2xl md:text-3xl mb-8">
                Business details — <span style={{ color: BUYER_BADGES[category].to }}>{BUYER_BADGES[category].label}</span>
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <Input label="Business name" testid="buyer-business-name" name="business_name" required value={form.business_name} onChange={onChange} />
                <Input label="Contact person" testid="buyer-contact-person" name="contact_person" required value={form.contact_person} onChange={onChange} />
                <Input label="Email" testid="buyer-email" name="email" type="email" required value={form.email} onChange={onChange} />
                <Input label="Phone" testid="buyer-phone" name="phone" required value={form.phone} onChange={onChange} />
                <div className="md:col-span-1">
                  <Input label="GST number" testid="buyer-gst" name="gst_number" required value={form.gst_number} onChange={onChange} onBlur={() => setGstErr(validateGstin(form.gst_number))} />
                  {gstErr && <p data-testid="buyer-gst-error" className="font-body text-xs text-[#C0392B] mt-2">{gstErr}</p>}
                </div>
                <Input label="Password (min 6 chars)" testid="buyer-password" name="password" type="password" required minLength={6} value={form.password} onChange={onChange} />
                <Textarea label="Business address" testid="buyer-address" name="address" required rows={2} value={form.address} onChange={onChange} className="md:col-span-2" />
                <Textarea label={CATEGORY_INFO_LABEL[category]} testid="buyer-category-info" name="category_info" required rows={2} value={form.category_info} onChange={onChange} className="md:col-span-2" />
              </div>

              {/* Custodian-only bank + GST panel */}
              {category === "custodian" && (
                <div className="mt-10 rounded-sm border border-[#A78BFA]/25 bg-[#0a0a12] p-6 md:p-8 relative overflow-hidden" data-testid="custodian-bank-panel">
                  <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#A78BFA] to-transparent opacity-60" />
                  <div className="flex items-start gap-3 mb-5">
                    <span className="grid place-items-center w-9 h-9 rounded-full border border-[#A78BFA]/50 text-[#A78BFA] shrink-0">
                      <KeyRound size={15} />
                    </span>
                    <div>
                      <div className="font-serif text-lg text-[#F5F5F0]">Capital bank account · Vinci-Sponsored GST</div>
                      <p className="font-body text-xs text-[#8A8A82] mt-1 leading-relaxed max-w-xl">
                        Custodians hold the running & trade capital account for a dependent Super-Stockist. We auto-flag your GST arrangement as <b className="text-[#A78BFA]">Vinci-Sponsored &amp; Backed</b> when the state on your GSTIN is a small state / UT eligible for the Sole-Proprietorship model.
                      </p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input label="Account holder name" testid="cust-bank-holder" name="bank_holder_name" required value={form.bank_holder_name} onChange={onChange} />
                    <Input label="Bank name" testid="cust-bank-name" name="bank_name" required value={form.bank_name} onChange={onChange} />
                    <Input label="IFSC code" testid="cust-ifsc" name="ifsc" required value={form.ifsc} onChange={onChange} />
                    <Input label="Account number" testid="cust-account-number" name="account_number" required value={form.account_number} onChange={onChange} />
                  </div>
                </div>
              )}

              {/* Super-Stockist ↔ Level-2 sponsor picker */}
              {category === "super_stockist" && (
                <div className="mt-10 rounded-sm border border-white/10 bg-[#0a0a0a] p-6 md:p-8" data-testid="ss-sponsor-panel">
                  {invite ? (
                    <div data-testid="invite-locked-sponsor" className="flex items-start gap-3">
                      <span className="grid place-items-center w-9 h-9 rounded-full border border-[#2FB999]/50 text-[#2FB999] shrink-0">
                        <Mail size={15} />
                      </span>
                      <div>
                        <div className="font-serif text-lg text-[#F5F5F0]">Registering via invite from <span className="text-[#B87333]">{invite.sponsor_business_name}</span></div>
                        <p className="font-body text-xs text-[#8A8A82] mt-1 leading-relaxed">
                          You'll be linked as a <b className="text-[#F5F5F0]">3{invite.sponsor_category === "introducer" ? "B" : invite.sponsor_category === "guarantor" ? "C" : "D"}</b> dependent — {invite.sponsor_category.charAt(0).toUpperCase() + invite.sponsor_category.slice(1)}-Dependent.
                          Your orders will queue in <b>{invite.sponsor_business_name}</b>'s approval inbox before Vinci Admin.
                        </p>
                        {invite.note && (
                          <blockquote className="mt-3 border-l-2 border-[#B87333]/50 pl-3 font-body text-xs text-[#C9C9C2] italic">"{invite.note}"</blockquote>
                        )}
                        <div className="mt-3 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">
                          Sponsor vendor code · <span className="text-[#B87333]">{invite.sponsor_vendor_code}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <SponsorPicker value={sponsor} onChange={setSponsor} />
                  )}
                </div>
              )}

              {inviteError && (
                <div data-testid="invite-error" className="mt-6 border border-[#C0392B]/50 bg-[#C0392B]/[0.05] rounded-sm p-4 font-body text-sm text-[#C0392B]">
                  {inviteError}
                </div>
              )}

              <div className="mt-10">
                <SubmitButton testid="buyer-submit" type="submit" disabled={loading}>
                  {loading ? "Creating…" : "Create buyer account"}
                </SubmitButton>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
      <SecurityPulseGate
        open={pulseOpen}
        email={form.email}
        purpose="signup"
        displayName={form.contact_person || form.business_name}
        onClose={() => setPulseOpen(false)}
        onVerified={finishRegister}
      />
    </RegisterLayout>
  );
}
